import { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Chip,
  Divider,
  Collapse,
  Switch,
  FormControlLabel,
  InputAdornment,
  CircularProgress,
  Checkbox,
  Radio,
  Tooltip,
  Autocomplete,
  FormControl,
  InputLabel,
  MenuItem,
} from '@mui/material';
import SearchableSelect from 'components/Forms/searchable/SearchableSelect';
import { priceTransform, formatNumber } from '../../../helpers/formPrice';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  SwapVert as SwapVertIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  ContentCopy as ContentCopyIcon,
  Close as CloseIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import ModifierGroupSortModal from '../../../components/PosMenu/ModifierGroupSortModal';
import ConnectedProductsPickerDialog from '../../../components/PosMenu/ConnectedProductsPickerDialog';
import { toast } from 'react-toastify';
import { getUniqueSourceOptions } from '../../../utils/posMenuNestedSources';
import { validateSelectionLimits } from '../../../utils/posMenuSelectionLimits';
import confirm from '../../../components/Confirm';
import TranslationInput from '../../../components/PosMenu/TranslationInput';
import ModifierGroupOptionsManager from './ModifierGroupOptionsManager';
import {
  createModifierGroup,
  updateModifierGroup,
  createItemModifierGroup,
  deleteItemModifierGroup,
  createModifierGroupOption,
  bulkCreateModifierGroupOptions,
  createItem,
  updateItem,
  deleteItem,
} from '../../../redux/actions/posMenu';
import {
  DEFAULT_MODIFIER_GROUP,
  getTranslationValue,
  PRICE_TYPE_OPTIONS,
  IN_ORDER_VIEW_LAYOUT_OPTIONS,
} from '../../../constants/posMenu';

// Helper function to find all ancestor items (items that have the given item as an option)
const getItemAncestors = (itemId, menuMap) => {
  const ancestors = new Set();
  const visited = new Set();

  const findParents = (targetId) => {
    if (visited.has(targetId)) return;
    visited.add(targetId);

    // Check all items to see if any have this item as an option
    Object.values(menuMap?.items || {}).forEach((parentItem) => {
      (parentItem.modifierGroupIds || []).forEach((mgId) => {
        const mg = menuMap?.modifierGroups?.[mgId];
        if (mg?.optionIds?.includes(targetId)) {
          ancestors.add(parentItem._id);
          findParents(parentItem._id); // Recursively find parents of parents
        }
      });
    });
  };

  findParents(itemId);
  return ancestors;
};

// Helper function to find all descendant items (options and nested options) of a modifier group
const getMgDescendants = (mgId, menuMap) => {
  const descendants = new Set();
  const visited = new Set();

  const findDescendants = (modifierGroupId) => {
    if (visited.has(modifierGroupId)) return;
    visited.add(modifierGroupId);

    const mg = menuMap?.modifierGroups?.[modifierGroupId];
    if (!mg?.optionIds) return;

    // Add all direct options
    mg.optionIds.forEach((optionId) => {
      descendants.add(optionId);

      // For each option, check if it has modifier groups and recurse
      const optionItem = menuMap?.items?.[optionId];
      if (optionItem?.modifierGroupIds) {
        optionItem.modifierGroupIds.forEach((nestedMgId) => {
          findDescendants(nestedMgId);
        });
      }
    });
  };

  findDescendants(mgId);
  return descendants;
};

const ModifierGroupManager = ({ item, menuMap }) => {
  const dispatch = useDispatch();
  const internalMenuId = useSelector((state) => state.posMenu.internalMenuId);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingModifier, setEditingModifier] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_MODIFIER_GROUP);
  const [expandedModifiers, setExpandedModifiers] = useState({});
  const [attachDialogOpen, setAttachDialogOpen] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [displayCount, setDisplayCount] = useState(10);

  // State for adding options during modifier group creation
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [optionSearchQuery, setOptionSearchQuery] = useState('');
  const [optionDisplayCount, setOptionDisplayCount] = useState(10);
  // State for tracking which selected options should be preselected
  const [preselectedOptions, setPreselectedOptions] = useState(new Set());

  // State for creating new item in options selector
  const [isCreatingNewOption, setIsCreatingNewOption] = useState(false);
  const [newOptionName, setNewOptionName] = useState('');
  const [newOptionDescription, setNewOptionDescription] = useState('');
  const [newOptionPrice, setNewOptionPrice] = useState(0);
  const [newOptionPriceType, setNewOptionPriceType] = useState(null);
  const [newOptionHideNameOnOrder, setNewOptionHideNameOnOrder] =
    useState(false);
  const [newOptionShowNestedLayout, setNewOptionShowNestedLayout] =
    useState('default');
  const [newOptionInOrderViewLayout, setNewOptionInOrderViewLayout] =
    useState(null);

  // Track locally created option items (to handle timing issues with Redux)
  const [locallyCreatedOptionItems, setLocallyCreatedOptionItems] = useState(
    {}
  );

  // State for sort modal
  const [sortModalOpen, setSortModalOpen] = useState(false);

  // State for selected modifier groups in attach dialog
  const [selectedModifierGroups, setSelectedModifierGroups] = useState([]);

  // Loading states for various save operations
  const [isSavingNewModifier, setIsSavingNewModifier] = useState(false);
  const [isSavingModifierGroup, setIsSavingModifierGroup] = useState(false);
  const [isCreatingNewOptionItem, setIsCreatingNewOptionItem] = useState(false);
  const [isAttachingModifierGroups, setIsAttachingModifierGroups] =
    useState(false);
  const [pendingConnectedProducts, setPendingConnectedProducts] =
    useState(null);
  const [connectedProductsPickerOpen, setConnectedProductsPickerOpen] =
    useState(false);

  // State for nested modifier group creation
  const [isNested, setIsNested] = useState(false);
  const [sourceMgIds, setSourceMgIds] = useState([]); // Array of selected source modifier group IDs
  const [nestedOptions, setNestedOptions] = useState([]); // [{name: '', prices: {sourceOptionId: price}}]
  const [newNestedOptionName, setNewNestedOptionName] = useState('');
  const [newNestedOptionPrices, setNewNestedOptionPrices] = useState({}); // {sourceOptionId: price}
  const [attachAfterCreate, setAttachAfterCreate] = useState(true); // Whether to attach nested MGs to source options

  // Helper function to handle numeric input validation
  const handleNumericKeyDown = (e, allowDecimal = false) => {
    // Allow: backspace, delete, tab, escape, enter
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter'];
    if (allowDecimal) {
      allowedKeys.push('.');
    }
    if (
      allowedKeys.includes(e.key) ||
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.ctrlKey && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) ||
      // Allow: home, end, left, right
      ['Home', 'End', 'ArrowLeft', 'ArrowRight'].includes(e.key)
    ) {
      return;
    }
    // Block if not a number
    if (isNaN(Number(e.key))) {
      e.preventDefault();
    }
  };

  // Get modifier groups for this item from modifierGroupIds
  const itemModifierGroups = (item?.modifierGroupIds || [])
    .map((mgId) => menuMap?.modifierGroups?.[mgId])
    .filter(Boolean);

  // Convert all modifier groups object to array
  const allModifierGroups = menuMap?.modifierGroups
    ? Object.values(menuMap.modifierGroups)
    : [];

  // Get all items for option selection (merge menuMap items with locally created items)
  const allItems = useMemo(() => {
    const menuItems = menuMap?.items ? Object.values(menuMap.items) : [];
    const localItems = Object.values(locallyCreatedOptionItems);
    // Merge, avoiding duplicates (local items might already be in menuMap after Redux updates)
    const itemIds = new Set(menuItems.map((item) => item._id));
    const uniqueLocalItems = localItems.filter(
      (item) => !itemIds.has(item._id)
    );
    return [...menuItems, ...uniqueLocalItems];
  }, [menuMap?.items, locallyCreatedOptionItems]);

  // Get items to exclude (current item and its ancestors) to prevent circular references
  const itemAndAncestors = useMemo(() => {
    const set = new Set([item._id]);
    getItemAncestors(item._id, menuMap).forEach((id) => set.add(id));
    return set;
  }, [item._id, menuMap]);

  // Filter items for option selection (exclude current item and its ancestors to prevent circular refs)
  const filteredOptionsForSelection = allItems.filter((optItem) => {
    // Exclude current item and its ancestors
    if (itemAndAncestors.has(optItem._id)) return false;
    if (!optionSearchQuery) return true;
    const name = getTranslationValue(optItem.name).toLowerCase();
    return name.includes(optionSearchQuery.toLowerCase());
  });

  const displayedOptionsForSelection = filteredOptionsForSelection.slice(
    0,
    optionDisplayCount
  );
  const hasMoreOptions =
    filteredOptionsForSelection.length > optionDisplayCount;

  // Helper function to clean preselectOptions by removing stale modifier groups and options
  // Preserves nested preselection composite keys (format: optionItemId_nestedModGroupId)
  const cleanPreselectOptions = (currentOptions) => {
    if (!currentOptions || !item?.modifierGroupIds) {
      return {};
    }

    const cleanedOptions = {};
    const validModifierGroupIds = new Set(item.modifierGroupIds);

    for (const [key, selections] of Object.entries(currentOptions)) {
      // Check if this is a composite key for nested preselections (contains underscore)
      // Composite keys have format: optionItemId_nestedModGroupId
      const isCompositeKey = key.includes('_');

      if (isCompositeKey) {
        // For composite keys (nested selections), preserve them as-is
        // These are managed by the NestedModifierSelector component
        if (selections && selections.length > 0) {
          cleanedOptions[key] = selections;
        }
        continue;
      }

      // For top-level modifier group keys, validate against item's modifier groups
      if (!validModifierGroupIds.has(key)) {
        continue;
      }

      const modGroup = menuMap?.modifierGroups?.[key];
      if (!modGroup) {
        continue;
      }

      // Get valid option IDs for this modifier group
      const validOptionIds = new Set(modGroup.optionIds || []);

      // Filter out options that are no longer in this modifier group
      const cleanedSelections = (selections || []).filter((sel) =>
        validOptionIds.has(sel._id)
      );

      // Only keep the modifier group entry if it has valid selections
      if (cleanedSelections.length > 0) {
        cleanedOptions[key] = cleanedSelections;
      }
    }

    return cleanedOptions;
  };

  const handleToggleExpand = (modifierId) => {
    setExpandedModifiers((prev) => ({
      ...prev,
      [modifierId]: !prev[modifierId],
    }));
  };

  const handleOpenDialog = (modifierGroup = null) => {
    if (modifierGroup) {
      setEditingModifier(modifierGroup);
      setFormData(modifierGroup);
    } else {
      setEditingModifier(null);
      setFormData(DEFAULT_MODIFIER_GROUP);
    }
    setPendingConnectedProducts(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingModifier(null);
    setFormData(DEFAULT_MODIFIER_GROUP);
    setPendingConnectedProducts(null);
  };

  const handleOpenAttachDialog = () => {
    setAttachDialogOpen(true);
    setIsCreatingNew(false);
    setSearchQuery('');
    setDisplayCount(10);
    setFormData(DEFAULT_MODIFIER_GROUP);
  };

  // Items made with "Create & Select" exist as soon as they're created. Any
  // that didn't end up as an option of some modifier group are deleted when
  // the dialog closes, so cancelling doesn't leave orphan items behind.
  const discardUnattachedCreatedItems = async (createdItems, attachedIds) => {
    const keep = new Set([
      ...attachedIds,
      ...Object.values(menuMap?.modifierGroups || {}).flatMap(
        (group) => group.optionIds || []
      ),
    ]);
    const orphans = Object.values(createdItems).filter(
      (created) => !keep.has(created._id)
    );
    if (orphans.length === 0) return;
    const results = await Promise.allSettled(
      orphans.map((orphan) => dispatch(deleteItem(orphan._id)))
    );
    const failedNames = orphans
      .filter((_, i) => results[i].status === 'rejected')
      .map((orphan) => getTranslationValue(orphan.name));
    if (failedNames.length > 0) {
      toast.error(
        `Couldn't delete unused new item(s): ${failedNames.join(', ')}`
      );
    }
  };

  const handleCloseAttachDialog = () => {
    discardUnattachedCreatedItems(locallyCreatedOptionItems, []);
    resetAttachDialog();
  };

  const resetAttachDialog = () => {
    setAttachDialogOpen(false);
    setIsCreatingNew(false);
    setSearchQuery('');
    setDisplayCount(10);
    setSelectedOptions([]);
    setPreselectedOptions(new Set());
    setOptionSearchQuery('');
    setOptionDisplayCount(10);
    setIsCreatingNewOption(false);
    setNewOptionName('');
    setNewOptionPrice(0);
    setNewOptionPriceType(null);
    setNewOptionHideNameOnOrder(false);
    setNewOptionShowNestedLayout('default');
    setNewOptionInOrderViewLayout(null);
    setLocallyCreatedOptionItems({});
    setSelectedModifierGroups([]);
    // Reset nested modifier state
    setIsNested(false);
    setSourceMgIds([]);
    setNestedOptions([]);
    setNewNestedOptionName('');
    setNewNestedOptionPrices({});
    setAttachAfterCreate(true);
    // Reset form data for modifier group creation
    setFormData(DEFAULT_MODIFIER_GROUP);
  };

  const handleCreateNewFromAttach = () => {
    setIsCreatingNew(true);
    setFormData(DEFAULT_MODIFIER_GROUP);
  };

  const handleSaveNewModifierFromAttach = async () => {
    try {
      if (!formData.name?.en?.trim()) {
        toast.error('Name is Required');
        return;
      }
      const limitsError = validateSelectionLimits(formData);
      if (limitsError) {
        toast.error(limitsError);
        return;
      }

      setIsSavingNewModifier(true);

      // Handle nested modifier creation
      if (isNested && sourceMgIds.length > 0) {
        // Filter out options with empty names
        const validOptions = nestedOptions.filter((opt) => opt.name?.trim());

        if (validOptions.length === 0) {
          toast.error(
            'Please add at least one option with a name for nested modifier groups'
          );
          setIsSavingNewModifier(false);
          return;
        }

        const allSourceOptionIds = getUniqueSourceOptions(
          sourceMgIds,
          menuMap?.modifierGroups
        ).map(({ optId }) => optId);

        if (allSourceOptionIds.length === 0) {
          toast.error('Selected source modifier groups have no options');
          setIsSavingNewModifier(false);
          return;
        }

        // Create all MGs in parallel for speed
        const mgCreationPromises = allSourceOptionIds.map(
          async (sourceOptionId) => {
            const sourceOption = menuMap?.items?.[sourceOptionId];
            const sourceOptionName =
              getTranslationValue(sourceOption?.name) || 'Option';

            // 1. Create new MG - name stays as user set, title gets option name appended
            const titleBase = formData.title?.en || formData.name.en;
            const newMgTitle = `${titleBase} - ${sourceOptionName}`;
            const newMg = await dispatch(
              createModifierGroup({
                name: { en: formData.name.en },
                title: { en: newMgTitle },
                description: formData.description,
                min: formData.min ?? null,
                max: formData.max ?? null,
                free: formData.free ?? null,
                isOnePrice: formData.isOnePrice,
                priceType: formData.priceType ?? null,
                tags: formData.tags,
                sort: formData.sort,
                canSelectMultipleOptions: formData.canSelectMultipleOptions,
                maxAnySingleModifierItems:
                  formData.maxAnySingleModifierItems ?? null,
                maxTotalNumberOfModifierItems:
                  formData.maxTotalNumberOfModifierItems ?? null,
                minAnySingleModifierItems:
                  formData.minAnySingleModifierItems ?? null,
                minTotalNumberOfModifierItems:
                  formData.minTotalNumberOfModifierItems ?? null,
                hideNameOnOrder: formData.hideNameOnOrder ?? false,
                showNestedLayout: formData.showNestedLayout ?? 'default',
                inOrderViewLayout: formData.inOrderViewLayout ?? null,
                applyConditionWithMultiplier:
                  formData.applyConditionWithMultiplier ?? null,
                conditionIsAppliedForProduct:
                  formData.conditionIsAppliedForProduct ?? null,
                applyValidationToItemsActualQuantity:
                  formData.applyValidationToItemsActualQuantity ?? null,
                sortParentOptionsByThisOptionsSorting:
                  formData.sortParentOptionsByThisOptionsSorting ?? null,
                groupAllParentsByThisOption:
                  formData.groupAllParentsByThisOption ?? null,
                menu: internalMenuId,
              })
            );

            // 2. Create all items in parallel for this MG
            const itemCreationPromises = validOptions.map((opt, optIndex) => {
              const priceForThisMg = opt.prices?.[sourceOptionId] || 0;
              return dispatch(
                createItem({
                  name: { en: opt.name },
                  title: { en: `${opt.name} ${newMgTitle}` },
                  description: { en: '' },
                  price: priceForThisMg,
                  priceType: null,
                  tax: null,
                  sort: 0,
                  tags: [],
                  isOnePrice: false,
                  image: { url: '', fileId: '' },
                  soldOut: { status: 'available', soldOutDate: null },
                  onItsOwn: false,
                  isSizeOption: false,
                  kitchenInstruction: { en: '' },
                  recipe: { en: '' },
                  minAllowedQuantity: null,
                  maxAllowedQuantity: null,
                  minTotalNumberOfModifierGroups: null,
                  maxTotalNumberOfModifierGroups: null,
                  minTotalNumberOfModifierItems: null,
                  maxTotalNumberOfModifierItems: null,
                  isPopular: false,
                  scheduleIds: [],
                  printerTags: [],
                  menu: internalMenuId,
                  hideNameOnOrder: false,
                  showNestedLayout: 'default',
                  inOrderViewLayout: null,
                })
              );
            });

            const createdItems = await Promise.all(itemCreationPromises);

            // 3. Attach all items to MG at once with correct sorting
            await dispatch(
              bulkCreateModifierGroupOptions(
                newMg._id,
                createdItems.map((item, index) => ({
                  optionId: item._id,
                  sort: index,
                })),
                internalMenuId
              )
            );

            // 4. Attach this MG to the source option (e.g., Small, Medium, Large) - only if attachAfterCreate is true.
            // No sort: the backend appends it after the option's existing groups.
            if (attachAfterCreate) {
              await dispatch(
                createItemModifierGroup({
                  itemId: sourceOptionId,
                  modifierGroupId: newMg._id,
                  menuId: internalMenuId,
                })
              );
            }

            return newMg;
          }
        );

        const results = await Promise.allSettled(mgCreationPromises);
        const failedSourceNames = allSourceOptionIds
          .filter((_, index) => results[index].status === 'rejected')
          .map(
            (id) => getTranslationValue(menuMap?.items?.[id]?.name) || 'Option'
          );
        const createdMgCount = results.length - failedSourceNames.length;

        if (createdMgCount === 0) {
          toast.error(
            results[0].reason?.message ||
              'Failed to create nested modifier groups'
          );
          setIsSavingNewModifier(false);
          return;
        }

        if (failedSourceNames.length > 0) {
          toast.warning(
            `${createdMgCount} modifier group(s) created. Failed for: ${failedSourceNames.join(
              ', '
            )}. Check those options before retrying, they may be partially set up.`
          );
        } else {
          toast.success(
            attachAfterCreate
              ? `${createdMgCount} modifier group(s) created and attached successfully`
              : `${createdMgCount} modifier group(s) created successfully`
          );
        }
        // Close so a retry can't recreate the groups that succeeded
        handleCloseAttachDialog();
        setFormData(DEFAULT_MODIFIER_GROUP);
        return;
      }

      // Regular (non-nested) modifier group creation
      const createData = {
        name: formData.name,
        title: formData.title,
        description: formData.description,
        min: formData.min ?? null,
        max: formData.max ?? null,
        free: formData.free ?? null,
        isOnePrice: formData.isOnePrice,
        priceType: formData.priceType ?? null,
        tags: formData.tags,
        sort: formData.sort,
        canSelectMultipleOptions: formData.canSelectMultipleOptions,
        maxAnySingleModifierItems: formData.maxAnySingleModifierItems ?? null,
        maxTotalNumberOfModifierItems:
          formData.maxTotalNumberOfModifierItems ?? null,
        minAnySingleModifierItems: formData.minAnySingleModifierItems ?? null,
        minTotalNumberOfModifierItems:
          formData.minTotalNumberOfModifierItems ?? null,
        hideNameOnOrder: formData.hideNameOnOrder ?? false,
        showNestedLayout: formData.showNestedLayout ?? 'default',
        inOrderViewLayout: formData.inOrderViewLayout ?? null,
        applyConditionWithMultiplier:
          formData.applyConditionWithMultiplier ?? null,
        conditionIsAppliedForProduct:
          formData.conditionIsAppliedForProduct ?? null,
        applyValidationToItemsActualQuantity:
          formData.applyValidationToItemsActualQuantity ?? null,
        sortParentOptionsByThisOptionsSorting:
          formData.sortParentOptionsByThisOptionsSorting ?? null,
        groupAllParentsByThisOption:
          formData.groupAllParentsByThisOption ?? null,
        menu: internalMenuId,
      };
      const newModifierGroup = await dispatch(createModifierGroup(createData));

      // Add selected options to the modifier group
      if (selectedOptions.length > 0) {
        for (let i = 0; i < selectedOptions.length; i++) {
          await dispatch(
            createModifierGroupOption({
              modifierGroupId: newModifierGroup._id,
              optionId: selectedOptions[i],
              menuId: internalMenuId,
              sort: i,
            })
          );
        }
      }

      await dispatch(
        createItemModifierGroup({
          itemId: item._id,
          modifierGroupId: newModifierGroup._id,
          menuId: internalMenuId,
        })
      );

      // Handle preselected options - update parent item's preselectOptions
      if (preselectedOptions.size > 0 && item) {
        const currentPreselectOptions = item.preselectOptions || {
          defaultSelection: false,
          options: {},
        };

        // Check if this is radio mode (single selection only)
        const isRadioMode = formData.min === 1 && formData.max === 1;

        let newSelections;
        if (isRadioMode) {
          // Radio mode: only keep the last preselected item
          const lastPreselectedId = Array.from(preselectedOptions).pop();
          newSelections = [{ _id: lastPreselectedId, count: 1, sticky: false }];
        } else {
          // Multi-select mode: add all preselected items
          newSelections = Array.from(preselectedOptions).map((optionId) => ({
            _id: optionId,
            count: 1,
            sticky: false,
          }));
          // Respect max selection limit if set
          if (formData.max && newSelections.length > formData.max) {
            newSelections = newSelections.slice(-formData.max);
          }
        }

        const newPreselectOptions = {
          ...currentPreselectOptions,
          defaultSelection: true,
          options: {
            ...currentPreselectOptions.options,
            [newModifierGroup._id]: newSelections,
          },
        };

        await dispatch(
          updateItem(item._id, { preselectOptions: newPreselectOptions })
        );
      }

      const optionsMessage =
        selectedOptions.length > 0
          ? ` with ${selectedOptions.length} option(s)`
          : '';
      const preselectedMessage =
        preselectedOptions.size > 0
          ? ` (${preselectedOptions.size} preselected)`
          : '';
      toast.success(
        `Modifier group created${optionsMessage}${preselectedMessage} and attached successfully`
      );
      discardUnattachedCreatedItems(locallyCreatedOptionItems, selectedOptions);
      resetAttachDialog();
      setFormData(DEFAULT_MODIFIER_GROUP);
    } catch (error) {
      toast.error(error.message || 'An error occurred');
    } finally {
      setIsSavingNewModifier(false);
    }
  };

  const handleCreateNewOptionAndSelect = async () => {
    try {
      if (!newOptionName.trim()) {
        toast.error('Item name is required');
        return;
      }

      setIsCreatingNewOptionItem(true);

      // Create the new item
      // Title = option name + modifier group title
      const mgTitle = formData.title?.en || formData.name?.en || '';
      const newItem = await dispatch(
        createItem({
          name: { en: newOptionName },
          title: { en: `${newOptionName} ${mgTitle}`.trim() },
          description: { en: newOptionDescription },
          price: parseFloat(newOptionPrice) || 0,
          priceType: newOptionPriceType,
          tax: null,
          sort: 0,
          tags: [],
          isOnePrice: false,
          image: { url: '', key: '' },
          soldOut: { status: 'available', soldOutDate: null },
          onItsOwn: false,
          isActive: true,
          isPopular: false,
          scheduleIds: [],
          printerTags: [],
          menu: internalMenuId,
          minAllowedQuantity: null,
          maxAllowedQuantity: null,
          minTotalNumberOfModifierGroups: null,
          maxTotalNumberOfModifierGroups: null,
          minTotalNumberOfModifierItems: null,
          maxTotalNumberOfModifierItems: null,
          hideNameOnOrder: newOptionHideNameOnOrder,
          showNestedLayout: newOptionShowNestedLayout,
          inOrderViewLayout: newOptionInOrderViewLayout,
        })
      );

      // Add the new item to locallyCreatedOptionItems for immediate display
      setLocallyCreatedOptionItems((prev) => ({
        ...prev,
        [newItem._id]: newItem,
      }));

      // Add to selected options
      setSelectedOptions((prev) => [...prev, newItem._id]);

      toast.success(`Item "${newOptionName}" created and selected`);
      setIsCreatingNewOption(false);
      setNewOptionName('');
      setNewOptionDescription('');
      setNewOptionPrice(0);
      setNewOptionPriceType(null);
      setNewOptionHideNameOnOrder(false);
      setNewOptionShowNestedLayout('default');
      setNewOptionInOrderViewLayout(null);
    } catch (error) {
      toast.error(error.message || 'An error occurred');
    } finally {
      setIsCreatingNewOptionItem(false);
    }
  };

  const handleSaveModifierGroup = async () => {
    try {
      if (!formData.name?.en?.trim()) {
        toast.error('Name is Required');
        return;
      }
      const limitsError = validateSelectionLimits(formData);
      if (limitsError) {
        toast.error(limitsError);
        return;
      }

      setIsSavingModifierGroup(true);

      let modifierGroupId;
      if (editingModifier && editingModifier._id) {
        // Filter out read-only and computed fields before updating
        const updateData = {
          name: formData.name,
          title: formData.title,
          description: formData.description,
          min: formData.min ?? null,
          max: formData.max ?? null,
          free: formData.free ?? null,
          isOnePrice: formData.isOnePrice,
          priceType: formData.priceType ?? null,
          tags: formData.tags,
          sort: formData.sort,
          canSelectMultipleOptions: formData.canSelectMultipleOptions,
          maxAnySingleModifierItems: formData.maxAnySingleModifierItems ?? null,
          maxTotalNumberOfModifierItems:
            formData.maxTotalNumberOfModifierItems ?? null,
          minAnySingleModifierItems: formData.minAnySingleModifierItems ?? null,
          minTotalNumberOfModifierItems:
            formData.minTotalNumberOfModifierItems ?? null,
          hideNameOnOrder: formData.hideNameOnOrder ?? false,
          showNestedLayout: formData.showNestedLayout ?? 'default',
          inOrderViewLayout: formData.inOrderViewLayout ?? null,
          applyConditionWithMultiplier:
            formData.applyConditionWithMultiplier ?? null,
          conditionIsAppliedForProduct:
            formData.conditionIsAppliedForProduct ?? null,
          applyValidationToItemsActualQuantity:
            formData.applyValidationToItemsActualQuantity ?? null,
          sortParentOptionsByThisOptionsSorting:
            formData.sortParentOptionsByThisOptionsSorting ?? null,
          groupAllParentsByThisOption:
            formData.groupAllParentsByThisOption ?? null,
          preselectOptions: formData.preselectOptions,
        };
        await dispatch(updateModifierGroup(editingModifier._id, updateData));
        modifierGroupId = editingModifier._id;

        // Save pending connected products changes if any
        if (pendingConnectedProducts !== null) {
          const currentConnectedItems = Object.values(
            menuMap?.items || {}
          ).filter((menuItem) =>
            menuItem.modifierGroupIds?.includes(modifierGroupId)
          );

          const currentIds = new Set(
            currentConnectedItems.map((item) => item._id)
          );
          const newIds = new Set(
            pendingConnectedProducts.map((item) => item._id)
          );

          const addedProducts = pendingConnectedProducts.filter(
            (item) => !currentIds.has(item._id)
          );
          const removedProducts = currentConnectedItems.filter(
            (item) => !newIds.has(item._id)
          );

          const failedConnect = [];
          const failedRemove = [];

          // Add new products. No sort: the backend appends it after the
          // product's existing groups.
          for (const product of addedProducts) {
            try {
              await dispatch(
                createItemModifierGroup({
                  itemId: product._id,
                  modifierGroupId: modifierGroupId,
                  menuId: internalMenuId,
                })
              );
            } catch (error) {
              failedConnect.push(product);
            }
          }

          // Remove products
          for (const product of removedProducts) {
            try {
              await dispatch(
                deleteItemModifierGroup({
                  itemId: product._id,
                  modifierGroupId: modifierGroupId,
                })
              );
            } catch (error) {
              failedRemove.push(product);
            }
          }

          if (failedConnect.length > 0 || failedRemove.length > 0) {
            const productNames = (products) =>
              products
                .map((p) => getTranslationValue(p.name) || p._id)
                .join(', ');
            // Keep the dialog open with the pending list, so saving again
            // only retries the failed products
            toast.error(
              [
                'Modifier group updated, but some connected products failed.',
                failedConnect.length > 0 &&
                  `Couldn't connect: ${productNames(failedConnect)}.`,
                failedRemove.length > 0 &&
                  `Couldn't remove: ${productNames(failedRemove)}.`,
              ]
                .filter(Boolean)
                .join(' ')
            );
            return;
          }
        }

        toast.success('Modifier group updated successfully');
      } else {
        const createData = {
          name: formData.name,
          title: formData.title,
          description: formData.description,
          min: formData.min ?? null,
          max: formData.max ?? null,
          free: formData.free ?? null,
          isOnePrice: formData.isOnePrice,
          priceType: formData.priceType ?? null,
          tags: formData.tags,
          sort: formData.sort,
          canSelectMultipleOptions: formData.canSelectMultipleOptions,
          maxAnySingleModifierItems: formData.maxAnySingleModifierItems ?? null,
          maxTotalNumberOfModifierItems:
            formData.maxTotalNumberOfModifierItems ?? null,
          minAnySingleModifierItems: formData.minAnySingleModifierItems ?? null,
          minTotalNumberOfModifierItems:
            formData.minTotalNumberOfModifierItems ?? null,
          hideNameOnOrder: formData.hideNameOnOrder ?? false,
          showNestedLayout: formData.showNestedLayout ?? 'default',
          inOrderViewLayout: formData.inOrderViewLayout ?? null,
          applyConditionWithMultiplier:
            formData.applyConditionWithMultiplier ?? null,
          conditionIsAppliedForProduct:
            formData.conditionIsAppliedForProduct ?? null,
          applyValidationToItemsActualQuantity:
            formData.applyValidationToItemsActualQuantity ?? null,
          sortParentOptionsByThisOptionsSorting:
            formData.sortParentOptionsByThisOptionsSorting ?? null,
          groupAllParentsByThisOption:
            formData.groupAllParentsByThisOption ?? null,
          menu: internalMenuId,
        };
        const newModifierGroup = await dispatch(
          createModifierGroup(createData)
        );
        modifierGroupId = newModifierGroup._id;
        toast.success('Modifier group created successfully');
      }

      // If it's a new modifier group and we're in the context of an item, attach it
      if (!editingModifier && item && item._id) {
        await dispatch(
          createItemModifierGroup({
            itemId: item._id,
            modifierGroupId,
            menuId: internalMenuId,
          })
        );
      }

      handleCloseDialog();
    } catch (error) {
      toast.error(error.message || 'An error occurred');
    } finally {
      setIsSavingModifierGroup(false);
    }
  };

  const handleAttachExisting = async (modifierGroupId) => {
    try {
      await dispatch(
        createItemModifierGroup({
          itemId: item._id,
          modifierGroupId,
          menuId: internalMenuId,
        })
      );
      toast.success('Modifier group attached successfully');
    } catch (error) {
      toast.error(error.message || 'An error occurred');
    }
  };

  const handleAttachSelectedModifierGroups = async () => {
    if (selectedModifierGroups.length === 0) {
      toast.error('Please select at least one modifier group');
      return;
    }

    setIsAttachingModifierGroups(true);
    try {
      for (let i = 0; i < selectedModifierGroups.length; i++) {
        await dispatch(
          createItemModifierGroup({
            itemId: item._id,
            modifierGroupId: selectedModifierGroups[i],
            menuId: internalMenuId,
          })
        );
      }
      toast.success(
        `${selectedModifierGroups.length} modifier group(s) attached successfully`
      );
      handleCloseAttachDialog();
    } catch (error) {
      toast.error(error.message || 'An error occurred');
    } finally {
      setIsAttachingModifierGroups(false);
    }
  };

  const handleToggleModifierGroup = (modifierGroupId) => {
    setSelectedModifierGroups((prev) =>
      prev.includes(modifierGroupId)
        ? prev.filter((id) => id !== modifierGroupId)
        : [...prev, modifierGroupId]
    );
  };

  const handleDetach = async (modifierGroupId) => {
    const confirmed = await confirm({
      title: 'Remove Modifier Group',
      message: 'Remove this modifier group from the item?',
      confirmText: 'Remove',
      cancelText: 'Cancel',
      confirmColor: 'error',
    });

    if (!confirmed) return;

    try {
      await dispatch(
        deleteItemModifierGroup({
          itemId: item._id,
          modifierGroupId,
        })
      );

      // Clean up preselectOptions - remove this modifier group from preselections
      if (item.preselectOptions?.options?.[modifierGroupId]) {
        const currentPreselectOptions = item.preselectOptions || {};

        // Clean up any stale modifier groups/options first
        const cleanedOptions = cleanPreselectOptions(
          currentPreselectOptions.options
        );

        // Remove the modifier group being deleted
        delete cleanedOptions[modifierGroupId];

        // If no more options are preselected, disable defaultSelection
        const hasAnyOptions = Object.keys(cleanedOptions).length > 0;
        const newPreselectOptions = {
          defaultSelection: hasAnyOptions
            ? currentPreselectOptions?.defaultSelection || false
            : false,
          options: cleanedOptions,
        };

        await dispatch(
          updateItem(item._id, { preselectOptions: newPreselectOptions })
        );
      }

      toast.success('Modifier group removed successfully');
    } catch (error) {
      toast.error(error.message || 'An error occurred');
    }
  };

  const handlePickerSave = (addedIds, removedIds) => {
    if (!editingModifier?._id) return;
    const currentConnected = Object.values(menuMap?.items || {}).filter(
      (menuItem) =>
        menuItem.modifierGroupIds?.includes(editingModifier._id)
    );
    const currentPending = pendingConnectedProducts ?? currentConnected;
    const currentIdSet = new Set(currentPending.map((p) => p._id));
    addedIds.forEach((id) => currentIdSet.add(id));
    removedIds.forEach((id) => currentIdSet.delete(id));
    const allItems = menuMap?.items || {};
    const newList = Array.from(currentIdSet)
      .map((id) => allItems[id])
      .filter(Boolean);
    setPendingConnectedProducts(newList);
    setConnectedProductsPickerOpen(false);
  };

  const unattachedModifierGroups = allModifierGroups.filter(
    (mg) =>
      !itemModifierGroups.some((img) => img._id === mg._id) &&
      // Exclude modifier groups that contain this item or its ancestors as options
      !(mg.optionIds || []).some((optId) => itemAndAncestors.has(optId))
  );

  // Filter modifier groups based on search query (name or internal name)
  const filteredModifierGroups = unattachedModifierGroups.filter((mg) => {
    if (!searchQuery) return true;
    const name = getTranslationValue(mg.name).toLowerCase();
    const title = getTranslationValue(mg.title)?.toLowerCase() || '';
    const query = searchQuery.toLowerCase();
    return name.includes(query) || title.includes(query);
  });

  // Get displayed modifier groups (with pagination)
  const displayedModifierGroups = filteredModifierGroups.slice(0, displayCount);
  const hasMore = filteredModifierGroups.length > displayCount;

  const handleLoadMore = () => {
    setDisplayCount((prev) => prev + 10);
  };

  return (
    <Box>
      {itemModifierGroups.length === 0 ? (
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          No modifier groups attached to this item yet.
        </Typography>
      ) : (
        <List dense>
          {itemModifierGroups.map((modifierGroup) => {
            const isExpanded = expandedModifiers[modifierGroup._id];
            // Use optionIds as the source of truth for options count
            // optionIds is maintained by the reducer and is always up-to-date
            const optionsCount = Array.isArray(modifierGroup.optionIds)
              ? modifierGroup.optionIds.length
              : Array.isArray(modifierGroup.options)
              ? modifierGroup.options.length
              : 0;

            return (
              <Box key={modifierGroup._id} sx={{ mb: 2 }}>
                <ListItem
                  secondaryAction={
                    <Box>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleToggleExpand(modifierGroup._id)}
                        title={isExpanded ? 'Collapse' : 'Expand options'}
                      >
                        {isExpanded ? (
                          <ExpandLessIcon fontSize="small" />
                        ) : (
                          <ExpandMoreIcon fontSize="small" />
                        )}
                      </IconButton>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleOpenDialog(modifierGroup)}
                        title="Edit"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleDetach(modifierGroup._id)}
                        title="Remove"
                      >
                        <CloseIcon
                          fontSize="small"
                          sx={{ color: 'error.main' }}
                        />
                      </IconButton>
                    </Box>
                  }
                  sx={{ bgcolor: '#f5f5f5', borderRadius: 1 }}
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        {getTranslationValue(modifierGroup.name)}
                        {getTranslationValue(modifierGroup.title) && (
                          <Typography
                            component="span"
                            sx={{ color: 'text.secondary', fontSize: '14px' }}
                          >
                            ({getTranslationValue(modifierGroup.title)})
                          </Typography>
                        )}
                        {optionsCount > 0 && (
                          <Chip
                            label={`${optionsCount} option(s)`}
                            size="small"
                            color="primary"
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box component="span">
                        Min: {modifierGroup.min ?? '-'} | Max:{' '}
                        {modifierGroup.max ?? '-'} | Free:{' '}
                        {modifierGroup.free ?? '-'}
                        {modifierGroup.canSelectMultipleOptions &&
                          ' | Multi-select'}
                      </Box>
                    }
                  />
                </ListItem>

                {/* Options Manager - shows items in this modifier group */}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <ModifierGroupOptionsManager
                    modifierGroup={modifierGroup}
                    menuMap={menuMap}
                    parentItem={item}
                  />
                </Collapse>
              </Box>
            );
          })}
        </List>
      )}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleOpenAttachDialog}
        >
          Add Modifier Group
        </Button>
        {itemModifierGroups.length > 1 && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<SwapVertIcon />}
            onClick={() => setSortModalOpen(true)}
          >
            Sort Modifier Groups
          </Button>
        )}
      </Box>

      {/* Sort Modal for Modifier Groups */}
      <ModifierGroupSortModal
        open={sortModalOpen}
        onClose={() => setSortModalOpen(false)}
        item={item}
        menuMap={menuMap}
      />

      {/* Add Modifier Group Dialog - Choose existing or create new */}
      <Dialog
        open={attachDialogOpen}
        onClose={handleCloseAttachDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '1.5rem',
            fontWeight: 600,
            pb: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          Add Modifier Group
        </DialogTitle>
        <DialogContent sx={{ overflow: 'auto' }}>
          {!isCreatingNew ? (
            <Box sx={{ pt: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateNewFromAttach}
                fullWidth
                size="large"
                sx={{
                  mb: 3,
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  boxShadow: 2,
                  '&:hover': {
                    boxShadow: 3,
                  },
                }}
              >
                Create New Modifier Group
              </Button>

              <Divider sx={{ my: 3 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                    letterSpacing: 1,
                  }}
                >
                  OR SELECT EXISTING ({unattachedModifierGroups.length})
                </Typography>
              </Divider>

              {unattachedModifierGroups.length === 0 ? (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}
                >
                  All modifier groups are already attached to this item
                </Typography>
              ) : (
                <Box>
                  <TextField
                    fullWidth
                    size="medium"
                    placeholder="Search modifier groups..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white',
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                    autoFocus
                  />

                  {filteredModifierGroups.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}
                    >
                      No modifier groups found matching "{searchQuery}"
                    </Typography>
                  ) : (
                    <>
                      <List
                        sx={{
                          maxHeight: 500,
                          overflowY: 'auto',
                          overflowX: 'hidden',
                          width: '100%',
                          px: '10px',
                        }}
                      >
                        {displayedModifierGroups.map((mg) => {
                          const isSelected = selectedModifierGroups.includes(
                            mg._id
                          );
                          return (
                            <ListItem
                              key={mg._id}
                              button
                              onClick={() => handleToggleModifierGroup(mg._id)}
                              selected={isSelected}
                              sx={{
                                border: isSelected
                                  ? '2px solid #1976d2'
                                  : '1px solid',
                                borderColor: isSelected ? '#1976d2' : 'divider',
                                borderRadius: 1.5,
                                mb: 1.5,
                                bgcolor: isSelected ? '#e3f2fd' : 'white',
                                width: '100%',
                                boxSizing: 'border-box',
                                '&:hover': {
                                  bgcolor: isSelected ? '#e3f2fd' : '#f5f5f5',
                                },
                              }}
                            >
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontWeight: 600,
                                      mb: 0.5,
                                      wordBreak: 'break-word',
                                      overflowWrap: 'break-word',
                                    }}
                                  >
                                    {getTranslationValue(mg.name)}
                                    {getTranslationValue(mg.title) && (
                                      <Typography
                                        component="span"
                                        sx={{
                                          color: 'text.secondary',
                                          fontWeight: 400,
                                          ml: 1,
                                        }}
                                      >
                                        ({getTranslationValue(mg.title)})
                                      </Typography>
                                    )}
                                  </Typography>
                                }
                                secondary={
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      wordBreak: 'break-word',
                                      overflowWrap: 'break-word',
                                    }}
                                  >
                                    Min: {mg.min ?? ''} | Max: {mg.max ?? ''} |
                                    Free: {mg.free ?? ''}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          );
                        })}
                      </List>

                      {hasMore && (
                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={handleLoadMore}
                          >
                            Load More (
                            {filteredModifierGroups.length - displayCount}{' '}
                            remaining)
                          </Button>
                        </Box>
                      )}

                      {selectedModifierGroups.length > 0 && (
                        <Typography
                          variant="body2"
                          sx={{
                            display: 'block',
                            textAlign: 'center',
                            mt: 2,
                            color: 'primary.main',
                            fontWeight: 600,
                          }}
                        >
                          {selectedModifierGroups.length} modifier group(s)
                          selected
                        </Typography>
                      )}

                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ display: 'block', textAlign: 'center', mt: 1 }}
                      >
                        Showing {displayedModifierGroups.length} of{' '}
                        {filteredModifierGroups.length}
                      </Typography>
                    </>
                  )}
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ pt: 1 }}>
              {/* Basic Information Section */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="overline"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                    letterSpacing: 1,
                    display: 'block',
                    mb: 2,
                  }}
                >
                  Basic Information
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <TranslationInput
                    label="Name"
                    value={formData.name || { en: '', es: '' }}
                    onChange={(value) =>
                      setFormData({ ...formData, name: value })
                    }
                    required
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <TranslationInput
                    label="Internal Name"
                    value={formData.title || { en: '', es: '' }}
                    onChange={(value) =>
                      setFormData({ ...formData, title: value })
                    }
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <TranslationInput
                    label="Description"
                    value={formData.description || { en: '', es: '' }}
                    onChange={(value) =>
                      setFormData({ ...formData, description: value })
                    }
                    multiline
                    rows={3}
                  />
                </Box>

                <Box sx={{ mb: 0 }}>
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="attach-mg-price-type-label">
                      Price Type
                    </InputLabel>
                    <SearchableSelect
                      labelId="attach-mg-price-type-label"
                      label="Price Type"
                      value={formData.priceType || ''}
                      displayEmpty
                      renderValue={(selected) => {
                        const option = PRICE_TYPE_OPTIONS.find(
                          (o) => o.value === selected
                        );
                        return option?.label || 'None';
                      }}
                      onChange={(e) => {
                        const newValue = e.target.value || null;
                        setFormData((prev) => ({
                          ...prev,
                          priceType: newValue,
                        }));
                      }}
                    >
                      {PRICE_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </SearchableSelect>
                  </FormControl>
                </Box>
              </Box>

              {/* Selection Rules Section */}
              <Box
                sx={{
                  bgcolor: '#f8f9fa',
                  borderRadius: 2,
                  p: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                    letterSpacing: 1,
                    display: 'block',
                    mb: 2,
                  }}
                >
                  Selection Rules
                </Typography>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="Minimum"
                    type="number"
                    value={formData.min ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData({
                        ...formData,
                        min: val === '' ? null : parseInt(val),
                      });
                    }}
                    onKeyDown={(e) => handleNumericKeyDown(e, false)}
                    onWheel={(e) => e.target.blur()}
                    fullWidth
                    size="medium"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: '0' }}
                    sx={{
                      bgcolor: 'white',
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  />

                  <TextField
                    label="Maximum"
                    type="number"
                    value={formData.max ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData({
                        ...formData,
                        max: val === '' ? null : parseInt(val),
                      });
                    }}
                    onKeyDown={(e) => handleNumericKeyDown(e, false)}
                    onWheel={(e) => e.target.blur()}
                    fullWidth
                    size="medium"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: '0' }}
                    sx={{
                      bgcolor: 'white',
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  />

                  <TextField
                    label="Free"
                    type="number"
                    value={formData.free ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData({
                        ...formData,
                        free: val === '' ? null : parseInt(val),
                      });
                    }}
                    onKeyDown={(e) => handleNumericKeyDown(e, false)}
                    onWheel={(e) => e.target.blur()}
                    fullWidth
                    size="medium"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: '0' }}
                    sx={{
                      bgcolor: 'white',
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  />
                </Box>

                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mt: 1.5,
                    color: 'text.secondary',
                    fontStyle: 'italic',
                  }}
                >
                  Define how many options customers can select from this group
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.canSelectMultipleOptions || false}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            canSelectMultipleOptions: e.target.checked,
                          })
                        }
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                          Allow Multiple Quantities
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: '#9E9EAD' }}>
                          Allows customers to select the same modifier option multiple times using quantity controls.
                        </Typography>
                      </Box>
                    }
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.hideNameOnOrder || false}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            hideNameOnOrder: e.target.checked,
                          })
                        }
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                          Hide Modifier Group Name on Orders
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: '#9E9EAD' }}>
                          Hides the modifier group name from order displays, receipts, and tickets.
                        </Typography>
                      </Box>
                    }
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={
                          formData.applyConditionWithMultiplier || false
                        }
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            applyConditionWithMultiplier: e.target.checked,
                          })
                        }
                      />
                    }
                    label={
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                            Use Option Multipliers for Selection Rules
                          </Typography>
                          <Tooltip title="- Modifier group must be Multiplier (not Fixed Price)" placement="top" arrow>
                            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                          </Tooltip>
                        </Box>
                        <Typography sx={{ fontSize: '12px', color: '#9E9EAD' }}>
                          Counts the multiplier value of each option when evaluating the group's selection rules.
                        </Typography>
                      </Box>
                    }
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={
                          formData.conditionIsAppliedForProduct || false
                        }
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            conditionIsAppliedForProduct: e.target.checked,
                          })
                        }
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                          Apply Selection Rules Across Product
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: '#9E9EAD' }}>
                          Combines selections for this modifier group across all locations in the product when evaluating its selection rules.
                        </Typography>
                      </Box>
                    }
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={
                          formData.applyValidationToItemsActualQuantity || false
                        }
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            applyValidationToItemsActualQuantity: e.target.checked,
                          })
                        }
                      />
                    }
                    label={
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                            Weigh Selections by Multipliers
                          </Typography>
                          <Tooltip
                            title={
                              <Box>
                                <Typography sx={{ fontSize: '12px' }}>- Modifier group must be Fixed Price</Typography>
                                <Typography sx={{ fontSize: '12px' }}>- Modifier group must have at least one of:</Typography>
                                <Typography sx={{ fontSize: '12px' }}>  • a nested Parent Multiplier modifier group</Typography>
                                <Typography sx={{ fontSize: '12px' }}>  • a parent Nested Multiplier modifier group</Typography>
                              </Box>
                            }
                            placement="top"
                            arrow
                          >
                            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                          </Tooltip>
                        </Box>
                        <Typography sx={{ fontSize: '12px', color: '#9E9EAD' }}>
                          Uses the multiplier values selected in the nested modifier group to evaluate this group's selection rules.
                        </Typography>
                      </Box>
                    }
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={
                          formData.sortParentOptionsByThisOptionsSorting || false
                        }
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            sortParentOptionsByThisOptionsSorting: e.target.checked,
                          })
                        }
                      />
                    }
                    label={
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                            Sort Parent Selections by This Group's Sorting
                          </Typography>
                          <Tooltip
                            title={
                              <Box>
                                <Typography sx={{ fontSize: '12px' }}>- Modifier group must be Parent Multiplier</Typography>
                                <Typography sx={{ fontSize: '12px' }}>- Modifier group must have Join Parent from Left or Join Parent from Right enabled</Typography>
                                <Typography sx={{ fontSize: '12px' }}>- Modifier group must be nested under a Fixed Price modifier group</Typography>
                              </Box>
                            }
                            placement="top"
                            arrow
                          >
                            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                          </Tooltip>
                        </Box>
                        <Typography sx={{ fontSize: '12px', color: '#9E9EAD' }}>
                          Orders parent modifier selections based on the sorting defined for this option in this modifier group.
                        </Typography>
                      </Box>
                    }
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={
                          formData.groupAllParentsByThisOption || false
                        }
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            groupAllParentsByThisOption: e.target.checked,
                          })
                        }
                      />
                    }
                    label={
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                            Group Parent Selections by This Option
                          </Typography>
                          <Tooltip
                            title={
                              <Box>
                                <Typography sx={{ fontSize: '12px' }}>- Modifier group must be Parent Multiplier</Typography>
                                <Typography sx={{ fontSize: '12px' }}>- Modifier group must be nested under a Fixed Price modifier group</Typography>
                              </Box>
                            }
                            placement="top"
                            arrow
                          >
                            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                          </Tooltip>
                        </Box>
                        <Typography sx={{ fontSize: '12px', color: '#9E9EAD' }}>
                          Displays parent modifier selections grouped under the options in this modifier group.
                        </Typography>
                      </Box>
                    }
                  />
                </Box>

                {/* Additional options when canSelectMultipleOptions is enabled */}
                {formData.canSelectMultipleOptions && (
                  <Box sx={{ mt: 3, pl: 2, borderLeft: '3px solid #1976d2' }}>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        mb: 2,
                        color: '#333',
                      }}
                    >
                      How many times can customers select any single option?
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                      <TextField
                        label="Min Any Single Options"
                        type="number"
                        value={formData.minAnySingleModifierItems ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData({
                            ...formData,
                            minAnySingleModifierItems:
                              val === '' ? null : parseInt(val),
                          });
                        }}
                        onKeyDown={(e) => handleNumericKeyDown(e, false)}
                        onWheel={(e) => e.target.blur()}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: '0' }}
                      />
                      <TextField
                        label="Max Any Single Options"
                        type="number"
                        value={formData.maxAnySingleModifierItems ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData({
                            ...formData,
                            maxAnySingleModifierItems:
                              val === '' ? null : parseInt(val),
                          });
                        }}
                        onKeyDown={(e) => handleNumericKeyDown(e, false)}
                        onWheel={(e) => e.target.blur()}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: '0' }}
                      />
                    </Box>

                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        mb: 2,
                        color: '#333',
                      }}
                    >
                      What's the number of options a customer can select?
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField
                        label="Min Total Number of Modifier Items"
                        type="number"
                        value={formData.minTotalNumberOfModifierItems ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData({
                            ...formData,
                            minTotalNumberOfModifierItems:
                              val === '' ? null : parseInt(val),
                          });
                        }}
                        onKeyDown={(e) => handleNumericKeyDown(e, false)}
                        onWheel={(e) => e.target.blur()}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: '0' }}
                      />
                      <TextField
                        label="Max Total Number of Modifier Items"
                        type="number"
                        value={formData.maxTotalNumberOfModifierItems ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData({
                            ...formData,
                            maxTotalNumberOfModifierItems:
                              val === '' ? null : parseInt(val),
                          });
                        }}
                        onKeyDown={(e) => handleNumericKeyDown(e, false)}
                        onWheel={(e) => e.target.blur()}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: '0' }}
                      />
                    </Box>
                  </Box>
                )}

                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
                    Nested Layout
                  </Typography>
                  <FormControl size="small" fullWidth>
                    <SearchableSelect
                      value={formData.showNestedLayout || 'default'}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          showNestedLayout: e.target.value,
                        })
                      }
                    >
                      <MenuItem value="default">Default</MenuItem>
                      <MenuItem value="popup">Popup</MenuItem>
                    </SearchableSelect>
                  </FormControl>
                  <Typography
                    sx={{ fontSize: '12px', color: '#9E9EAD', mt: 0.5 }}
                  >
                    Choose how nested modifiers are displayed
                  </Typography>
                </Box>

                {formData.priceType === 'multiplierApplyingToParentPrice' && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}
                    >
                      In-Order View Layout
                    </Typography>
                    <FormControl size="small" fullWidth>
                      <SearchableSelect
                        value={formData.inOrderViewLayout || ''}
                        displayEmpty
                        renderValue={(selected) => {
                          const option = IN_ORDER_VIEW_LAYOUT_OPTIONS.find(
                            (o) => o.value === selected
                          );
                          return option?.label || 'None';
                        }}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            inOrderViewLayout: e.target.value || null,
                          })
                        }
                      >
                        {IN_ORDER_VIEW_LAYOUT_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </SearchableSelect>
                    </FormControl>
                    <Typography
                      sx={{ fontSize: '12px', color: '#9E9EAD', mt: 0.5 }}
                    >
                      Controls how this modifier group is displayed in the order
                      view
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Nested Modifier Section */}
              <Box
                sx={{
                  mt: 3,
                  bgcolor: '#fff8e1',
                  borderRadius: 2,
                  p: 2.5,
                  border: '1px solid',
                  borderColor: '#ffe082',
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isNested}
                      onChange={(e) => {
                        setIsNested(e.target.checked);
                        if (!e.target.checked) {
                          setSourceMgIds([]);
                          setNestedOptions([]);
                          setNewNestedOptionName('');
                          setNewNestedOptionPrices({});
                          setAttachAfterCreate(true);
                        }
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                        Is Nested Modifier
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: '#666' }}>
                        Create multiple modifier groups based on another
                        modifier group's options
                      </Typography>
                    </Box>
                  }
                />

                {isNested && (
                  <Box sx={{ mt: 2 }}>
                    {/* Source MG Dropdown - Multi-select */}
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mb: 1,
                        fontWeight: 600,
                        color: 'text.secondary',
                      }}
                    >
                      Select Source Modifier Group(s)
                    </Typography>
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      options={allModifierGroups.filter(
                        (mg) => (mg.optionIds?.length || 0) > 0
                      )}
                      value={allModifierGroups.filter((mg) =>
                        sourceMgIds.includes(mg._id)
                      )}
                      onChange={(event, newValue) => {
                        setSourceMgIds(newValue.map((mg) => mg._id));
                        // Reset options and prices when source MGs change
                        setNestedOptions([]);
                        setNewNestedOptionName('');
                        setNewNestedOptionPrices({});
                      }}
                      getOptionLabel={(option) => {
                        const name =
                          getTranslationValue(option.name) || 'Unnamed';
                        const internalName = getTranslationValue(option.title);
                        return internalName
                          ? `${name} (${internalName})`
                          : name;
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option._id === value._id
                      }
                      filterOptions={(options, { inputValue }) => {
                        const searchTerm = inputValue.toLowerCase();
                        return options.filter((option) => {
                          const name = (
                            getTranslationValue(option.name) || ''
                          ).toLowerCase();
                          const internalName = (
                            getTranslationValue(option.title) || ''
                          ).toLowerCase();
                          return (
                            name.includes(searchTerm) ||
                            internalName.includes(searchTerm)
                          );
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={
                            sourceMgIds.length === 0
                              ? 'Select modifier group(s)...'
                              : ''
                          }
                          size="small"
                          sx={{ bgcolor: 'white' }}
                        />
                      )}
                      renderOption={(props, option, { selected }) => {
                        const name =
                          getTranslationValue(option.name) || 'Unnamed';
                        const internalName = getTranslationValue(option.title);
                        const optionsCount = option.optionIds?.length || 0;
                        return (
                          <li {...props} key={option._id}>
                            <Checkbox
                              checked={selected}
                              size="small"
                              sx={{ mr: 1 }}
                            />
                            <Box>
                              <Typography sx={{ fontWeight: 500 }}>
                                {name}
                                {internalName && (
                                  <Typography
                                    component="span"
                                    sx={{
                                      ml: 0.5,
                                      color: 'text.secondary',
                                      fontWeight: 400,
                                    }}
                                  >
                                    ({internalName})
                                  </Typography>
                                )}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {optionsCount} option(s)
                              </Typography>
                            </Box>
                          </li>
                        );
                      }}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option._id}
                            label={
                              getTranslationValue(option.name) || 'Unnamed'
                            }
                            size="small"
                          />
                        ))
                      }
                      sx={{ mb: 2 }}
                    />

                    {/* Show selected MG options as chips */}
                    {sourceMgIds.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mb: 1,
                            fontWeight: 600,
                            color: 'text.secondary',
                          }}
                        >
                          Source Options (will create one MG per option):
                        </Typography>
                        <Box
                          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                        >
                          {getUniqueSourceOptions(
                            sourceMgIds,
                            menuMap?.modifierGroups
                          ).map(({ optId, mgNames }) => {
                            const optionItem = menuMap?.items?.[optId];
                            return (
                              <Chip
                                key={optId}
                                label={`${
                                  getTranslationValue(optionItem?.name) ||
                                  'Unnamed'
                                } (${mgNames.join(', ')})`}
                                size="small"
                                sx={{ bgcolor: '#e3f2fd' }}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    )}

                    {/* Inline Options UI */}
                    {sourceMgIds.length > 0 && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          bgcolor: 'white',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mb: 2,
                            fontWeight: 600,
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                          }}
                        >
                          Add Options for Nested MGs
                        </Typography>

                        {/* Inline editable options - two line layout */}
                        <Box>
                          {/* Editable rows */}
                          {nestedOptions.map((opt, index) => {
                            // Collect all source option IDs from all selected MGs
                            const allSourceOptionIds = getUniqueSourceOptions(
                              sourceMgIds,
                              menuMap?.modifierGroups
                            );

                            return (
                              <Box
                                key={index}
                                sx={{
                                  mb: 2,
                                  p: 1.5,
                                  bgcolor: '#f9f9f9',
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                }}
                              >
                                {/* Line 1: Option Name + Copy + Delete */}
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 1.5,
                                  }}
                                >
                                  <TextField
                                    value={opt.name}
                                    onChange={(e) => {
                                      setNestedOptions((prev) =>
                                        prev.map((item, i) =>
                                          i === index
                                            ? { ...item, name: e.target.value }
                                            : item
                                        )
                                      );
                                    }}
                                    size="small"
                                    placeholder="Option name"
                                    label="Option Name"
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ flex: 1 }}
                                  />
                                  <Tooltip title="Copy option with empty name">
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setNestedOptions((prev) => {
                                          const optionToCopy = prev[index];
                                          const newOption = {
                                            name: '', // Empty name
                                            prices: { ...optionToCopy.prices }, // Copy prices
                                          };
                                          // Insert copy after the original
                                          return [
                                            ...prev.slice(0, index + 1),
                                            newOption,
                                            ...prev.slice(index + 1),
                                          ];
                                        });
                                      }}
                                      sx={{ color: 'primary.main' }}
                                    >
                                      <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setNestedOptions((prev) =>
                                        prev.filter((_, i) => i !== index)
                                      );
                                    }}
                                    sx={{ color: 'error.main' }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>

                                {/* Line 2: Prices for all source options */}
                                <Box
                                  sx={{
                                    display: 'grid',
                                    gridTemplateColumns: `repeat(${Math.min(
                                      allSourceOptionIds.length || 1,
                                      4
                                    )}, 1fr)`,
                                    gap: 1,
                                  }}
                                >
                                  {allSourceOptionIds.map(
                                    ({ optId, mgNames }) => {
                                      const optionItem =
                                        menuMap?.items?.[optId];
                                      const label =
                                        sourceMgIds.length > 1
                                          ? `${
                                              getTranslationValue(
                                                optionItem?.name
                                              ) || 'N/A'
                                            } (${mgNames.join(', ')})`
                                          : getTranslationValue(
                                              optionItem?.name
                                            ) || 'N/A';
                                      return (
                                        <TextField
                                          key={optId}
                                          label={label}
                                          value={formatNumber(
                                            opt.prices?.[optId] || 0
                                          )}
                                          onChange={(e) => {
                                            const transformed = priceTransform(
                                              e.target.value
                                            );
                                            setNestedOptions((prev) =>
                                              prev.map((item, i) =>
                                                i === index
                                                  ? {
                                                      ...item,
                                                      prices: {
                                                        ...item.prices,
                                                        [optId]: transformed,
                                                      },
                                                    }
                                                  : item
                                              )
                                            );
                                          }}
                                          size="small"
                                          InputLabelProps={{ shrink: true }}
                                          InputProps={{
                                            startAdornment: (
                                              <InputAdornment position="start">
                                                $
                                              </InputAdornment>
                                            ),
                                          }}
                                        />
                                      );
                                    }
                                  )}
                                </Box>
                              </Box>
                            );
                          })}

                          {/* Add row button */}
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => {
                              setNestedOptions((prev) => [
                                ...prev,
                                { name: '', prices: {} },
                              ]);
                            }}
                            sx={{ textTransform: 'none' }}
                          >
                            Add Option
                          </Button>
                        </Box>

                        {nestedOptions.length === 0 && (
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              mt: 1,
                              color: 'text.secondary',
                              fontStyle: 'italic',
                            }}
                          >
                            Click "Add Option" to add options for the nested
                            modifier groups
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Attach option toggle */}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={attachAfterCreate}
                          onChange={(e) =>
                            setAttachAfterCreate(e.target.checked)
                          }
                        />
                      }
                      label={
                        <Box>
                          <Typography
                            sx={{ fontSize: '14px', fontWeight: 500 }}
                          >
                            Attach to source options after creation
                          </Typography>
                          <Typography sx={{ fontSize: '12px', color: '#666' }}>
                            When unchecked, modifier groups will be created but
                            not attached
                          </Typography>
                        </Box>
                      }
                      sx={{ mt: 2 }}
                    />
                  </Box>
                )}
              </Box>

              {/* Add Options Section - Hidden when nested is selected */}
              {!isNested && (
                <Box sx={{ mt: 3 }}>
                  <Typography
                    variant="overline"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 600,
                      letterSpacing: 1,
                      display: 'block',
                      mb: 2,
                    }}
                  >
                    Add Options (Optional)
                  </Typography>

                  {!isCreatingNewOption ? (
                    <>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setIsCreatingNewOption(true)}
                        fullWidth
                        sx={{
                          mb: 2,
                          textTransform: 'none',
                          boxShadow: 1,
                        }}
                      >
                        Create New Item
                      </Button>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          my: 2,
                        }}
                      >
                        <Box
                          sx={{ flex: 1, height: '1px', bgcolor: 'divider' }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontWeight: 600,
                          }}
                        >
                          OR SELECT EXISTING
                        </Typography>
                        <Box
                          sx={{ flex: 1, height: '1px', bgcolor: 'divider' }}
                        />
                      </Box>

                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Search items to add as options..."
                        value={optionSearchQuery}
                        onChange={(e) => setOptionSearchQuery(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ mb: 2 }}
                      />

                      {/* Show selected items at the top */}
                      {selectedOptions.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="caption"
                            color="primary"
                            sx={{
                              display: 'block',
                              mb: 1,
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: 0.5,
                            }}
                          >
                            {selectedOptions.length} Selected
                          </Typography>
                          <List disablePadding sx={{ px: '10px' }}>
                            {selectedOptions.map((selectedId) => {
                              const selectedItem = allItems.find(
                                (i) => i._id === selectedId
                              );
                              if (!selectedItem) return null;
                              const isPreselected =
                                preselectedOptions.has(selectedId);
                              return (
                                <ListItem
                                  key={selectedId}
                                  button
                                  onClick={() => {
                                    setSelectedOptions((prev) =>
                                      prev.filter((id) => id !== selectedId)
                                    );
                                    // Also remove from preselected if removing from selected
                                    setPreselectedOptions((prev) => {
                                      const newSet = new Set(prev);
                                      newSet.delete(selectedId);
                                      return newSet;
                                    });
                                  }}
                                  selected
                                  sx={{
                                    mb: 1,
                                    border: '2px solid #1976d2',
                                    borderRadius: 1,
                                    bgcolor: '#e3f2fd',
                                  }}
                                >
                                  <ListItemText
                                    primary={getTranslationValue(
                                      selectedItem.name
                                    )}
                                    secondary={`Price: $${(
                                      Number(selectedItem.price) || 0
                                    ).toFixed(2)}`}
                                  />
                                  <Tooltip
                                    title={
                                      isPreselected
                                        ? 'Remove from preselected'
                                        : 'Set as default preselected'
                                    }
                                  >
                                    <Checkbox
                                      checked={isPreselected}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setPreselectedOptions((prev) => {
                                          const newSet = new Set(prev);
                                          if (newSet.has(selectedId)) {
                                            newSet.delete(selectedId);
                                          } else {
                                            newSet.add(selectedId);
                                          }
                                          return newSet;
                                        });
                                      }}
                                      size="small"
                                      icon={<StarBorderIcon />}
                                      checkedIcon={
                                        <StarIcon sx={{ color: '#ffc107' }} />
                                      }
                                      sx={{ ml: 1 }}
                                      inputProps={{
                                        'aria-label': 'Preselect this option',
                                      }}
                                    />
                                  </Tooltip>
                                </ListItem>
                              );
                            })}
                          </List>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              my: 2,
                            }}
                          >
                            <Box
                              sx={{
                                flex: 1,
                                height: '1px',
                                bgcolor: 'divider',
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                fontWeight: 500,
                              }}
                            >
                              Available (
                              {
                                filteredOptionsForSelection.filter(
                                  (item) => !selectedOptions.includes(item._id)
                                ).length
                              }
                              )
                            </Typography>
                            <Box
                              sx={{
                                flex: 1,
                                height: '1px',
                                bgcolor: 'divider',
                              }}
                            />
                          </Box>
                        </Box>
                      )}

                      {displayedOptionsForSelection.filter(
                        (item) => !selectedOptions.includes(item._id)
                      ).length === 0 && selectedOptions.length === 0 ? (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{
                            fontStyle: 'italic',
                            textAlign: 'center',
                            py: 2,
                          }}
                        >
                          {optionSearchQuery
                            ? `No items found matching "${optionSearchQuery}"`
                            : 'No items available'}
                        </Typography>
                      ) : displayedOptionsForSelection.filter(
                          (item) => !selectedOptions.includes(item._id)
                        ).length > 0 ? (
                        <>
                          <List
                            sx={{
                              maxHeight: selectedOptions.length > 0 ? 300 : 400,
                              overflow: 'auto',
                              px: '10px',
                            }}
                          >
                            {displayedOptionsForSelection
                              .filter(
                                (item) => !selectedOptions.includes(item._id)
                              )
                              .map((optionItem) => {
                                return (
                                  <ListItem
                                    key={optionItem._id}
                                    button
                                    onClick={() => {
                                      const itemId = optionItem._id;
                                      setSelectedOptions((prev) => [
                                        ...prev,
                                        itemId,
                                      ]);
                                    }}
                                    sx={{
                                      mb: 1,
                                      border: '1px solid #e0e0e0',
                                      borderRadius: 1,
                                      bgcolor: 'white',
                                    }}
                                  >
                                    <ListItemText
                                      primary={getTranslationValue(
                                        optionItem.name
                                      )}
                                      secondary={`Price: $${(
                                        Number(optionItem.price) || 0
                                      ).toFixed(2)}`}
                                    />
                                  </ListItem>
                                );
                              })}
                          </List>

                          {hasMoreOptions && (
                            <Box sx={{ textAlign: 'center', mt: 2 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() =>
                                  setOptionDisplayCount((prev) => prev + 10)
                                }
                              >
                                Load More (
                                {filteredOptionsForSelection.length -
                                  optionDisplayCount}{' '}
                                remaining)
                              </Button>
                            </Box>
                          )}

                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              mt: 1.5,
                              color: 'text.secondary',
                              fontStyle: 'italic',
                            }}
                          >
                            💡 You can also add options later by editing the
                            modifier group
                          </Typography>
                        </>
                      ) : null}
                    </>
                  ) : (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 2 }}>
                        Create New Item
                      </Typography>

                      <TextField
                        label="Name"
                        fullWidth
                        value={newOptionName}
                        onChange={(e) => setNewOptionName(e.target.value)}
                        required
                        autoFocus
                        sx={{ mb: 2 }}
                        InputLabelProps={{ shrink: true }}
                      />

                      <TextField
                        label="Description"
                        fullWidth
                        value={newOptionDescription}
                        onChange={(e) =>
                          setNewOptionDescription(e.target.value)
                        }
                        multiline
                        rows={2}
                        sx={{ mb: 2 }}
                        InputLabelProps={{ shrink: true }}
                      />

                      <TextField
                        label="Price"
                        type="text"
                        fullWidth
                        value={
                          newOptionPrice != null
                            ? formatNumber(newOptionPrice)
                            : ''
                        }
                        onChange={(e) => {
                          const transformed = priceTransform(e.target.value);
                          setNewOptionPrice(transformed);
                        }}
                        sx={{ mb: 2 }}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {newOptionPriceType &&
                              newOptionPriceType !== 'fixPrice'
                                ? '×'
                                : '$'}
                            </InputAdornment>
                          ),
                        }}
                      />

                      <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                        <InputLabel id="new-option-price-type-label">
                          Price Type
                        </InputLabel>
                        <SearchableSelect
                          labelId="new-option-price-type-label"
                          label="Price Type"
                          value={newOptionPriceType || ''}
                          displayEmpty
                          renderValue={(selected) => {
                            const option = PRICE_TYPE_OPTIONS.find(
                              (o) => o.value === selected
                            );
                            return option?.label || 'None';
                          }}
                          onChange={(e) => {
                            const newValue = e.target.value || null;
                            setNewOptionPriceType(newValue);
                          }}
                        >
                          {PRICE_TYPE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </SearchableSelect>
                      </FormControl>

                      <FormControlLabel
                        control={
                          <Switch
                            checked={newOptionHideNameOnOrder}
                            onChange={(e) =>
                              setNewOptionHideNameOnOrder(e.target.checked)
                            }
                          />
                        }
                        label={
                          <Box>
                            <Typography
                              sx={{ fontSize: '14px', fontWeight: 500 }}
                            >
                              Hide Name on Order
                            </Typography>
                            <Typography
                              sx={{ fontSize: '12px', color: '#9E9EAD' }}
                            >
                              Hide the option name on the order receipt
                            </Typography>
                          </Box>
                        }
                        sx={{ mb: 2 }}
                      />

                      <Box sx={{ mb: 2 }}>
                        <Typography
                          sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}
                        >
                          Nested Layout
                        </Typography>
                        <FormControl size="small" fullWidth>
                          <SearchableSelect
                            value={newOptionShowNestedLayout}
                            onChange={(e) =>
                              setNewOptionShowNestedLayout(e.target.value)
                            }
                          >
                            <MenuItem value="default">Default</MenuItem>
                            <MenuItem value="popup">Popup</MenuItem>
                          </SearchableSelect>
                        </FormControl>
                        <Typography
                          sx={{ fontSize: '12px', color: '#9E9EAD', mt: 0.5 }}
                        >
                          Choose how nested modifiers are displayed
                        </Typography>
                      </Box>

                      {newOptionPriceType ===
                        'multiplierApplyingToParentPrice' && (
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}
                          >
                            In-Order View Layout
                          </Typography>
                          <FormControl size="small" fullWidth>
                            <SearchableSelect
                              value={newOptionInOrderViewLayout || ''}
                              displayEmpty
                              renderValue={(selected) => {
                                const option =
                                  IN_ORDER_VIEW_LAYOUT_OPTIONS.find(
                                    (o) => o.value === selected
                                  );
                                return option?.label || 'None';
                              }}
                              onChange={(e) =>
                                setNewOptionInOrderViewLayout(
                                  e.target.value || null
                                )
                              }
                            >
                              {IN_ORDER_VIEW_LAYOUT_OPTIONS.map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </MenuItem>
                              ))}
                            </SearchableSelect>
                          </FormControl>
                          <Typography
                            sx={{
                              fontSize: '12px',
                              color: '#9E9EAD',
                              mt: 0.5,
                            }}
                          >
                            Controls how this option is displayed in the order
                            view
                          </Typography>
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          onClick={() => setIsCreatingNewOption(false)}
                          sx={{ textTransform: 'none' }}
                          disabled={isCreatingNewOptionItem}
                        >
                          Back
                        </Button>
                        <Button
                          onClick={handleCreateNewOptionAndSelect}
                          variant="contained"
                          disabled={
                            !newOptionName.trim() || isCreatingNewOptionItem
                          }
                          sx={{ textTransform: 'none', flex: 1 }}
                          startIcon={
                            isCreatingNewOptionItem ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <AddIcon />
                            )
                          }
                        >
                          {isCreatingNewOptionItem
                            ? 'Creating...'
                            : 'Create & Select'}
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: '#fafafa',
          }}
        >
          {isCreatingNew && (
            <Button
              onClick={() => setIsCreatingNew(false)}
              sx={{ mr: 'auto', textTransform: 'none' }}
              disabled={isSavingNewModifier}
            >
              Back
            </Button>
          )}
          <Button
            onClick={handleCloseAttachDialog}
            sx={{ textTransform: 'none' }}
            disabled={isSavingNewModifier || isAttachingModifierGroups}
          >
            Cancel
          </Button>
          {!isCreatingNew && selectedModifierGroups.length > 0 && (
            <Button
              onClick={handleAttachSelectedModifierGroups}
              variant="contained"
              color="primary"
              disabled={isAttachingModifierGroups}
              startIcon={
                isAttachingModifierGroups ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
              sx={{
                textTransform: 'none',
                px: 3,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 3,
                },
              }}
            >
              {isAttachingModifierGroups
                ? 'Adding...'
                : `Add Selected (${selectedModifierGroups.length})`}
            </Button>
          )}
          {isCreatingNew && (
            <Button
              onClick={handleSaveNewModifierFromAttach}
              variant="contained"
              color="primary"
              disabled={isSavingNewModifier}
              startIcon={
                isSavingNewModifier ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
              sx={{
                textTransform: 'none',
                px: 3,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 3,
                },
              }}
            >
              {isSavingNewModifier
                ? 'Saving...'
                : isNested && sourceMgIds.length > 0
                ? `Create ${
                    getUniqueSourceOptions(sourceMgIds, menuMap?.modifierGroups)
                      .length
                  } MG(s)${attachAfterCreate ? ' & Attach' : ''}`
                : 'Save & Attach'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Modifier Group Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '1.5rem',
            fontWeight: 600,
            pb: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          {editingModifier ? 'Edit Modifier Group' : 'Create Modifier Group'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {/* Basic Information Section */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="overline"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 600,
                  letterSpacing: 1,
                  display: 'block',
                  mb: 2,
                }}
              >
                Basic Information
              </Typography>

              <Box sx={{ mb: 2 }}>
                <TranslationInput
                  label="Name"
                  value={formData.name || { en: '', es: '' }}
                  onChange={(value) =>
                    setFormData({ ...formData, name: value })
                  }
                  required
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <TranslationInput
                  label="Internal Name"
                  value={formData.title || { en: '', es: '' }}
                  onChange={(value) =>
                    setFormData({ ...formData, title: value })
                  }
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <TranslationInput
                  label="Description"
                  value={formData.description || { en: '', es: '' }}
                  onChange={(value) =>
                    setFormData({ ...formData, description: value })
                  }
                  multiline
                  rows={3}
                />
              </Box>

              <Box sx={{ mb: 0 }}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id="mg-dialog-price-type-label">
                    Price Type
                  </InputLabel>
                  <SearchableSelect
                    labelId="mg-dialog-price-type-label"
                    label="Price Type"
                    value={formData.priceType || ''}
                    displayEmpty
                    renderValue={(selected) => {
                      const option = PRICE_TYPE_OPTIONS.find(
                        (o) => o.value === selected
                      );
                      return option?.label || 'None';
                    }}
                    onChange={(e) => {
                      const newValue = e.target.value || null;
                      setFormData((prev) => ({ ...prev, priceType: newValue }));
                    }}
                  >
                    {PRICE_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </SearchableSelect>
                </FormControl>
              </Box>
            </Box>

            {/* Selection Rules Section */}
            <Box
              sx={{
                bgcolor: '#f8f9fa',
                borderRadius: 2,
                p: 2.5,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 600,
                  letterSpacing: 1,
                  display: 'block',
                  mb: 2,
                }}
              >
                Selection Rules
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                <TextField
                  label="Minimum"
                  type="number"
                  value={formData.min ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData({
                      ...formData,
                      min: val === '' ? null : parseInt(val),
                    });
                  }}
                  onKeyDown={(e) => handleNumericKeyDown(e, false)}
                  onWheel={(e) => e.target.blur()}
                  fullWidth
                  size="medium"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: '0' }}
                  sx={{
                    bgcolor: 'white',
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />

                <TextField
                  label="Maximum"
                  type="number"
                  value={formData.max ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData({
                      ...formData,
                      max: val === '' ? null : parseInt(val),
                    });
                  }}
                  onKeyDown={(e) => handleNumericKeyDown(e, false)}
                  onWheel={(e) => e.target.blur()}
                  fullWidth
                  size="medium"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: '0' }}
                  sx={{
                    bgcolor: 'white',
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />

                <TextField
                  label="Free"
                  type="number"
                  value={formData.free ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData({
                      ...formData,
                      free: val === '' ? null : parseInt(val),
                    });
                  }}
                  onKeyDown={(e) => handleNumericKeyDown(e, false)}
                  onWheel={(e) => e.target.blur()}
                  fullWidth
                  size="medium"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: '0' }}
                  sx={{
                    bgcolor: 'white',
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />
              </Box>

              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: 'text.secondary',
                  fontStyle: 'italic',
                }}
              >
                Define how many options customers can select from this group
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.canSelectMultipleOptions || false}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          canSelectMultipleOptions: e.target.checked,
                        })
                      }
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                        Allow Multiple Quantities
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: '#9E9EAD' }}>
                        Allows customers to select the same modifier option multiple times using quantity controls.
                      </Typography>
                    </Box>
                  }
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.hideNameOnOrder || false}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          hideNameOnOrder: e.target.checked,
                        })
                      }
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                        Hide Modifier Group Name on Orders
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: '#9E9EAD' }}>
                        Hides the modifier group name from order displays, receipts, and tickets.
                      </Typography>
                    </Box>
                  }
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={
                        formData.applyConditionWithMultiplier || false
                      }
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          applyConditionWithMultiplier: e.target.checked,
                        })
                      }
                    />
                  }
                  label={
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                          Use Option Multipliers for Selection Rules
                        </Typography>
                        <Tooltip title="- Modifier group must be Multiplier (not Fixed Price)" placement="top" arrow>
                          <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                      <Typography sx={{ fontSize: '12px', color: '#9E9EAD' }}>
                        Counts the multiplier value of each option when evaluating the group's selection rules.
                      </Typography>
                    </Box>
                  }
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={
                        formData.conditionIsAppliedForProduct || false
                      }
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          conditionIsAppliedForProduct: e.target.checked,
                        })
                      }
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                        Apply Selection Rules Across Product
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: '#9E9EAD' }}>
                        Combines selections for this modifier group across all locations in the product when evaluating its selection rules.
                      </Typography>
                    </Box>
                  }
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={
                        formData.applyValidationToItemsActualQuantity || false
                      }
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          applyValidationToItemsActualQuantity: e.target.checked,
                        })
                      }
                    />
                  }
                  label={
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                          Weigh Selections by Multipliers
                        </Typography>
                        <Tooltip
                          title={
                            <Box>
                              <Typography sx={{ fontSize: '12px' }}>- Modifier group must be Fixed Price</Typography>
                              <Typography sx={{ fontSize: '12px' }}>- Modifier group must have at least one of:</Typography>
                              <Typography sx={{ fontSize: '12px' }}>  • a nested Parent Multiplier modifier group</Typography>
                              <Typography sx={{ fontSize: '12px' }}>  • a parent Nested Multiplier modifier group</Typography>
                            </Box>
                          }
                          placement="top"
                          arrow
                        >
                          <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                      <Typography sx={{ fontSize: '12px', color: '#9E9EAD' }}>
                        Uses the multiplier values selected in the nested modifier group to evaluate this group's selection rules.
                      </Typography>
                    </Box>
                  }
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={
                        formData.sortParentOptionsByThisOptionsSorting || false
                      }
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          sortParentOptionsByThisOptionsSorting: e.target.checked,
                        })
                      }
                    />
                  }
                  label={
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                          Sort Parent Selections by This Group's Sorting
                        </Typography>
                        <Tooltip
                          title={
                            <Box>
                              <Typography sx={{ fontSize: '12px' }}>- Modifier group must be Parent Multiplier</Typography>
                              <Typography sx={{ fontSize: '12px' }}>- Modifier group must have Join Parent from Left or Join Parent from Right enabled</Typography>
                              <Typography sx={{ fontSize: '12px' }}>- Modifier group must be nested under a Fixed Price modifier group</Typography>
                            </Box>
                          }
                          placement="top"
                          arrow
                        >
                          <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                      <Typography sx={{ fontSize: '12px', color: '#9E9EAD' }}>
                        Orders parent modifier selections based on the sorting defined for this option in this modifier group.
                      </Typography>
                    </Box>
                  }
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={
                        formData.groupAllParentsByThisOption || false
                      }
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          groupAllParentsByThisOption: e.target.checked,
                        })
                      }
                    />
                  }
                  label={
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                          Group Parent Selections by This Option
                        </Typography>
                        <Tooltip
                          title={
                            <Box>
                              <Typography sx={{ fontSize: '12px' }}>- Modifier group must be Parent Multiplier</Typography>
                              <Typography sx={{ fontSize: '12px' }}>- Modifier group must be nested under a Fixed Price modifier group</Typography>
                            </Box>
                          }
                          placement="top"
                          arrow
                        >
                          <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                      <Typography sx={{ fontSize: '12px', color: '#9E9EAD' }}>
                        Displays parent modifier selections grouped under the options in this modifier group.
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              {/* Additional options when canSelectMultipleOptions is enabled */}
              {formData.canSelectMultipleOptions && (
                <Box sx={{ mt: 3, pl: 2, borderLeft: '3px solid #1976d2' }}>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 500,
                      mb: 2,
                      color: '#333',
                    }}
                  >
                    How many times can customers select any single option?
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <TextField
                      label="Min Any Single Options"
                      type="number"
                      value={formData.minAnySingleModifierItems ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData({
                          ...formData,
                          minAnySingleModifierItems:
                            val === '' ? null : parseInt(val),
                        });
                      }}
                      onKeyDown={(e) => handleNumericKeyDown(e, false)}
                      onWheel={(e) => e.target.blur()}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: '0' }}
                    />
                    <TextField
                      label="Max Any Single Options"
                      type="number"
                      value={formData.maxAnySingleModifierItems ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData({
                          ...formData,
                          maxAnySingleModifierItems:
                            val === '' ? null : parseInt(val),
                        });
                      }}
                      onKeyDown={(e) => handleNumericKeyDown(e, false)}
                      onWheel={(e) => e.target.blur()}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: '0' }}
                    />
                  </Box>

                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 500,
                      mb: 2,
                      color: '#333',
                    }}
                  >
                    What's the number of options a customer can select?
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label="Min Total Number of Modifier Items"
                      type="number"
                      value={formData.minTotalNumberOfModifierItems ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData({
                          ...formData,
                          minTotalNumberOfModifierItems:
                            val === '' ? null : parseInt(val),
                        });
                      }}
                      onKeyDown={(e) => handleNumericKeyDown(e, false)}
                      onWheel={(e) => e.target.blur()}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: '0' }}
                    />
                    <TextField
                      label="Max Total Number of Modifier Items"
                      type="number"
                      value={formData.maxTotalNumberOfModifierItems ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData({
                          ...formData,
                          maxTotalNumberOfModifierItems:
                            val === '' ? null : parseInt(val),
                        });
                      }}
                      onKeyDown={(e) => handleNumericKeyDown(e, false)}
                      onWheel={(e) => e.target.blur()}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: '0' }}
                    />
                  </Box>
                </Box>
              )}

              <Box sx={{ mt: 2 }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
                  Nested Layout
                </Typography>
                <FormControl size="small" fullWidth>
                  <SearchableSelect
                    value={formData.showNestedLayout || 'default'}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        showNestedLayout: e.target.value,
                      })
                    }
                  >
                    <MenuItem value="default">Default</MenuItem>
                    <MenuItem value="popup">Popup</MenuItem>
                  </SearchableSelect>
                </FormControl>
                <Typography
                  sx={{ fontSize: '12px', color: '#9E9EAD', mt: 0.5 }}
                >
                  Choose how nested modifiers are displayed
                </Typography>
              </Box>

              {formData.priceType === 'multiplierApplyingToParentPrice' && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}
                  >
                    In-Order View Layout
                  </Typography>
                  <FormControl size="small" fullWidth>
                    <SearchableSelect
                      value={formData.inOrderViewLayout || ''}
                      displayEmpty
                      renderValue={(selected) => {
                        const option = IN_ORDER_VIEW_LAYOUT_OPTIONS.find(
                          (o) => o.value === selected
                        );
                        return option?.label || 'None';
                      }}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          inOrderViewLayout: e.target.value || null,
                        })
                      }
                    >
                      {IN_ORDER_VIEW_LAYOUT_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </SearchableSelect>
                  </FormControl>
                  <Typography
                    sx={{ fontSize: '12px', color: '#9E9EAD', mt: 0.5 }}
                  >
                    Controls how this modifier group is displayed in the order
                    view
                  </Typography>
                </Box>
              )}
            </Box>

            {!editingModifier && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 2,
                  p: 1.5,
                  bgcolor: '#e3f2fd',
                  borderRadius: 1,
                  color: 'primary.dark',
                }}
              >
                💡 Note: You can add options to this modifier group after
                creation
              </Typography>
            )}

            {/* Default Selections Section - only show when editing and has options */}
            {editingModifier &&
              editingModifier.optionIds?.length > 0 &&
              (() => {
                const mgId = editingModifier._id;
                const mgSelectedOptions =
                  formData.preselectOptions?.options?.[mgId] || [];

                const mgIsRadioMode =
                  formData.min === 1 && formData.max === 1;
                const mgShowQuantityInput =
                  !mgIsRadioMode && formData.canSelectMultipleOptions;
                const mgIsStickyEnabled = mgSelectedOptions.some(
                  (opt) => opt.sticky
                );

                const validOptionIds = new Set(editingModifier.optionIds);

                const cleanMgPreselectOptions = (currentOptions) => {
                  if (!currentOptions) return {};
                  const cleaned = {};
                  for (const [key, selections] of Object.entries(
                    currentOptions
                  )) {
                    if (key !== mgId) continue;
                    const cleanedSelections = (selections || []).filter(
                      (sel) => validOptionIds.has(sel._id)
                    );
                    if (cleanedSelections.length > 0) {
                      cleaned[key] = cleanedSelections;
                    }
                  }
                  return cleaned;
                };

                const handleMgPreselectToggle = (optionId) => {
                  let newSelectedOptions;
                  const existingOption = mgSelectedOptions.find(
                    (opt) => opt._id === optionId
                  );

                  if (mgIsRadioMode) {
                    newSelectedOptions = existingOption
                      ? []
                      : [{ _id: optionId, count: 1, sticky: false }];
                  } else {
                    if (existingOption) {
                      newSelectedOptions = mgSelectedOptions.filter(
                        (opt) => opt._id !== optionId
                      );
                    } else {
                      newSelectedOptions = [
                        ...mgSelectedOptions,
                        { _id: optionId, count: 1, sticky: false },
                      ];
                    }
                  }

                  const cleanedOptions = cleanMgPreselectOptions(
                    formData.preselectOptions?.options
                  );
                  setFormData({
                    ...formData,
                    preselectOptions: {
                      defaultSelection:
                        formData.preselectOptions?.defaultSelection || false,
                      options: {
                        ...cleanedOptions,
                        [mgId]: newSelectedOptions,
                      },
                    },
                  });
                };

                const handleMgPreselectQuantityInput = (optionId, val) => {
                  if (val === '' || parseInt(val) === 0) {
                    const newSelectedOptions = mgSelectedOptions.filter(
                      (opt) => opt._id !== optionId
                    );
                    setFormData({
                      ...formData,
                      preselectOptions: {
                        ...formData.preselectOptions,
                        options: {
                          ...formData.preselectOptions?.options,
                          [mgId]: newSelectedOptions,
                        },
                      },
                    });
                  } else {
                    const existingOption = mgSelectedOptions.find(
                      (opt) => opt._id === optionId
                    );
                    if (existingOption) {
                      const newSelectedOptions = mgSelectedOptions.map((opt) =>
                        opt._id === optionId
                          ? { ...opt, count: parseInt(val) }
                          : opt
                      );
                      setFormData({
                        ...formData,
                        preselectOptions: {
                          ...formData.preselectOptions,
                          options: {
                            ...formData.preselectOptions?.options,
                            [mgId]: newSelectedOptions,
                          },
                        },
                      });
                    } else {
                      const newSelectedOptions = [
                        ...mgSelectedOptions,
                        {
                          _id: optionId,
                          count: parseInt(val),
                          sticky: false,
                        },
                      ];
                      setFormData({
                        ...formData,
                        preselectOptions: {
                          ...formData.preselectOptions,
                          options: {
                            ...formData.preselectOptions?.options,
                            [mgId]: newSelectedOptions,
                          },
                        },
                      });
                    }
                  }
                };

                const handleMgPreselectStickyToggle = () => {
                  const currentSticky = mgSelectedOptions.some(
                    (opt) => opt.sticky
                  );
                  const newSelectedOptions = mgSelectedOptions.map((opt) => ({
                    ...opt,
                    sticky: !currentSticky,
                  }));
                  const cleanedOptions = cleanMgPreselectOptions(
                    formData.preselectOptions?.options
                  );
                  setFormData({
                    ...formData,
                    preselectOptions: {
                      defaultSelection:
                        formData.preselectOptions?.defaultSelection || false,
                      options: {
                        ...cleanedOptions,
                        [mgId]: newSelectedOptions,
                      },
                    },
                  });
                };

                return (
                  <Box sx={{ mt: 3 }}>
                    <Typography
                      variant="overline"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        letterSpacing: 1,
                        display: 'block',
                        mb: 1,
                      }}
                    >
                      Default Selections
                    </Typography>

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            formData.preselectOptions?.defaultSelection ||
                            false
                          }
                          onChange={(e) => {
                            const cleanedOptions = cleanMgPreselectOptions(
                              formData.preselectOptions?.options
                            );
                            setFormData({
                              ...formData,
                              preselectOptions: {
                                defaultSelection: e.target.checked,
                                options: cleanedOptions,
                              },
                            });
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{ fontSize: '14px', fontWeight: 500 }}
                        >
                          Enable Default Selections
                        </Typography>
                      }
                    />

                    {formData.preselectOptions?.defaultSelection && (
                      <Box
                        sx={{
                          mt: 1,
                          p: 2,
                          border: '1px solid #e0e0e0',
                          borderRadius: 2,
                          bgcolor: '#fafafa',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: 600,
                            mb: 2,
                            textTransform: 'uppercase',
                            color: '#666',
                          }}
                        >
                          {getTranslationValue(editingModifier.name)}
                        </Typography>

                        {editingModifier.optionIds.map((optionId) => {
                          const optionItem = menuMap?.items?.[optionId];
                          if (!optionItem) return null;

                          const isSelected = mgSelectedOptions.some(
                            (opt) => opt._id === optionId
                          );
                          const selectedOption = mgSelectedOptions.find(
                            (opt) => opt._id === optionId
                          );
                          const optionName =
                            getTranslationValue(optionItem.name) || 'Option';

                          return (
                            <Box
                              key={optionId}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                py: 1,
                                borderBottom: '1px solid #eee',
                              }}
                            >
                              {mgIsRadioMode ? (
                                <FormControlLabel
                                  control={
                                    <Radio
                                      checked={isSelected}
                                      onClick={() =>
                                        handleMgPreselectToggle(optionId)
                                      }
                                      size="small"
                                    />
                                  }
                                  label={
                                    <Typography sx={{ fontSize: '14px' }}>
                                      {optionName}
                                    </Typography>
                                  }
                                />
                              ) : mgShowQuantityInput ? (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    flex: 1,
                                  }}
                                >
                                  <Typography
                                    sx={{ fontSize: '14px', flex: 1 }}
                                  >
                                    {optionName}
                                  </Typography>
                                  <TextField
                                    size="small"
                                    type="number"
                                    label="Qty"
                                    value={selectedOption?.count || ''}
                                    onChange={(e) =>
                                      handleMgPreselectQuantityInput(
                                        optionId,
                                        e.target.value
                                      )
                                    }
                                    onWheel={(e) => e.target.blur()}
                                    inputProps={{ min: 0 }}
                                    sx={{ width: 80 }}
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Box>
                              ) : (
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={isSelected}
                                      onChange={() =>
                                        handleMgPreselectToggle(optionId)
                                      }
                                      size="small"
                                    />
                                  }
                                  label={
                                    <Typography sx={{ fontSize: '14px' }}>
                                      {optionName}
                                    </Typography>
                                  }
                                />
                              )}
                            </Box>
                          );
                        })}

                        {mgSelectedOptions.length > 0 && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={mgIsStickyEnabled}
                                onChange={handleMgPreselectStickyToggle}
                                size="small"
                              />
                            }
                            label={
                              <Box>
                                <Typography sx={{ fontSize: '13px' }}>
                                  Sticky selections
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: '11px',
                                    color: '#9E9EAD',
                                  }}
                                >
                                  Prevent customers from changing these
                                  preselected options
                                </Typography>
                              </Box>
                            }
                            sx={{ mt: 2 }}
                          />
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })()}

            {/* Connected Products Section - only show when editing */}
            {editingModifier &&
              (() => {
                const connectedItems = Object.values(
                  menuMap?.items || {}
                ).filter((menuItem) =>
                  menuItem.modifierGroupIds?.includes(editingModifier._id)
                );

                const itemsToExclude = getMgDescendants(
                  editingModifier._id,
                  menuMap
                );

                const allAvailableProducts = Object.values(
                  menuMap?.items || {}
                )
                  .filter((p) => !itemsToExclude.has(p._id))
                  .sort((a, b) => {
                    const nameA =
                      getTranslationValue(a.name)?.toLowerCase() || '';
                    const nameB =
                      getTranslationValue(b.name)?.toLowerCase() || '';
                    return nameA.localeCompare(nameB);
                  });

                const displayItems =
                  pendingConnectedProducts ?? connectedItems;
                const connectedProductIds = new Set(
                  displayItems.map((p) => p._id)
                );

                return (
                  <Box sx={{ mt: 3 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="overline"
                        sx={{
                          color: 'text.secondary',
                          fontWeight: 600,
                          letterSpacing: 1,
                        }}
                      >
                        Connected Products ({displayItems.length})
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          setConnectedProductsPickerOpen(true)
                        }
                        disabled={isSavingModifierGroup}
                      >
                        Manage Products
                      </Button>
                    </Box>
                    {displayItems.length === 0 ? (
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          fontStyle: 'italic',
                          py: 1,
                          textAlign: 'center',
                        }}
                      >
                        No products connected yet
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          maxHeight: 150,
                          overflowY: 'auto',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 0.5,
                          py: 0.5,
                        }}
                      >
                        {[...displayItems]
                          .sort((a, b) => {
                            const nameA =
                              getTranslationValue(a.name)?.toLowerCase() || '';
                            const nameB =
                              getTranslationValue(b.name)?.toLowerCase() || '';
                            return nameA.localeCompare(nameB);
                          })
                          .map((p) => {
                            const name = getTranslationValue(p.name);
                            const title = getTranslationValue(p.title);
                            const label = title
                              ? `${name} (${title})`
                              : name;
                            return (
                              <Chip
                                key={p._id}
                                label={label}
                                size="small"
                                onDelete={() => {
                                  const updated = displayItems.filter(
                                    (x) => x._id !== p._id
                                  );
                                  setPendingConnectedProducts(updated);
                                }}
                                disabled={isSavingModifierGroup}
                              />
                            );
                          })}
                      </Box>
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: 'text.secondary',
                        fontStyle: 'italic',
                        mt: 1,
                      }}
                    >
                      Changes will be saved when you click Update
                    </Typography>

                    <ConnectedProductsPickerDialog
                      open={connectedProductsPickerOpen}
                      onClose={() =>
                        setConnectedProductsPickerOpen(false)
                      }
                      allProducts={allAvailableProducts}
                      connectedProductIds={connectedProductIds}
                      categories={menuMap?.categories || {}}
                      modifierGroups={menuMap?.modifierGroups || {}}
                      onSave={handlePickerSave}
                      isSaving={false}
                    />
                  </Box>
                );
              })()}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: '#fafafa',
          }}
        >
          <Button
            onClick={handleCloseDialog}
            sx={{ textTransform: 'none' }}
            disabled={isSavingModifierGroup}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveModifierGroup}
            variant="contained"
            color="primary"
            disabled={isSavingModifierGroup}
            startIcon={
              isSavingModifierGroup ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
            sx={{
              textTransform: 'none',
              px: 3,
              boxShadow: 2,
              '&:hover': {
                boxShadow: 3,
              },
            }}
          >
            {isSavingModifierGroup
              ? 'Saving...'
              : editingModifier
              ? 'Update'
              : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ModifierGroupManager;
